import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CustomNumberFormatPipe } from '@wind-log_library/fe-component-library';
import { TranslateService } from '@ngx-translate/core';
import Highcharts from 'highcharts';
import { DEFAULT_CHART_OPTIONS } from 'app/shared/helper/highchart-options';
import { getSeriesSymbol } from 'app/shared/helper/highcharts.helper';
import { PowerCurveSummary } from 'app/analyze/model/historical-powercurves/chart-settings.model';
import { getHoveredCurvePoints } from 'app/analyze/components/historical-powercurves/power-curve-hover.helper';

const TOOLTIP_WINDOW_MARGIN = 8;

@Component({
  selector: 'app-power-curve',
  templateUrl: './power-curve.component.html',
  styleUrls: ['./power-curve.component.scss'],
  standalone: false,
})
export class PowerCurveComponent implements OnInit, OnChanges {
  @Input() scatterChartData: any[] = [];
  @Input() ratedPower: number;
  @Input() report: boolean;
  @Input() reportHeight: number | undefined;
  @Input() showColorLabel = false;
  @Input() hideTooltip = false;

  @Output() summaryChange = new EventEmitter<PowerCurveSummary>();
  @Output() summaryClick = new EventEmitter<PowerCurveSummary>();

  legendData: any;
  multiChartTooltip: Array<{ title; label; value }> = [];

  public chartOptions: Highcharts.Options;
  public highcharts = Highcharts;
  public chartRef: Highcharts.Chart | null = null;
  public updateFlag: boolean = false;

  constructor(
    private translate: TranslateService,
    private customNumberPipe: CustomNumberFormatPipe,
  ) {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hideTooltip && Object.keys(changes).length === 1 && this.chartRef) {
      this.chartRef.update({ tooltip: { enabled: !this.hideTooltip } });
      return;
    }
    this.setChartOptions();
  }

  private getSummary(hoveredPoint: Highcharts.Point): PowerCurveSummary {
    const { windSpeed, points } = getHoveredCurvePoints(hoveredPoint);
    return {
      windSpeed,
      rows: points.map(p => {
        // the series are built in setChartOptions as line/scatter with a plain string colour
        const options = p.series.userOptions as Highcharts.SeriesLineOptions;
        return {
          name: options.name,
          color: p.series.color as string,
          marker: p.series.type === 'scatter' ? 'circle' : (options.dashStyle === 'Dash' ? 'dashed' : 'solid'),
          value: p.y,
        };
      }),
    };
  }

  private emitSummary(hoveredPoint) {
    this.summaryChange.emit(this.getSummary(hoveredPoint));
  }

  private emitSummaryClick(clickedPoint) {
    if (this.hideTooltip && clickedPoint) {
      this.summaryClick.emit(this.getSummary(clickedPoint));
    }
  }

  updateDataset(datasetIndex) {
    this.scatterChartData[datasetIndex].hidden = !this.scatterChartData[datasetIndex].hidden;
    this.legendData[datasetIndex].hidden = !this.legendData[datasetIndex].hidden;
    this.setChartOptions();
  }

  public chartCallback: Highcharts.ChartCallbackFunction = (chart): void => {
    this.chartRef = chart;
  };

  private setChartOptions(): void {
    this.legendData = this.scatterChartData.map((a, i) => ({
      datasetIndex: i,
      fillStyle: a.borderColor,
      hidden: a.hidden || false,
      text: a.label,
      pointStyle: a.pointStyle === 'line' ? 'dash' : a.pointStyle || 'line',
    }));
    const customNumberPipe = this.customNumberPipe;
    const translate = this.translate;
    const report = this.report;
    const component = this;
    const formatNumber = (value: number) => customNumberPipe.transform(Number(value).toFixed(2));
    const getSymbol = (series): string => series.type === 'scatter'
      ? `<span style="display: inline-block; width: 8px; height: 8px; margin: 0 7px 0 4px; border-radius: 50%; background-color: ${series.color};"></span>`
      : getSeriesSymbol(series);
    const getAnalysisTooltip = (context): string => {
      const hoveredPoint = context.series ? context : (context.point ?? context.points[0]);
      const { windSpeed, points } = getHoveredCurvePoints(hoveredPoint);
      const rows = points.map(p => `
        <div style="display: flex; align-items: center;">
          ${getSymbol(p.series)}
          <span>${p.series.userOptions.name} | ${translate.instant('ANALYZE.PARK.OUTPUT_POWER')}: <b>${formatNumber(p.y)}MW</b></span>
        </div>
      `).join('');
      return `
        <div style="display: flex; flex-direction: column; gap: 10px; width: 366px; max-height: calc(100vh - ${2 * TOOLTIP_WINDOW_MARGIN}px); overflow: hidden; padding: 12px 8px; font-size: 12px; color: var(--font); white-space: normal;">
          <div>${translate.instant('ANALYZE.PARK.WIND_SPEED')}: <b>${formatNumber(windSpeed)}</b></div>
          ${rows}
        </div>
      `;
    };
    const series =this.scatterChartData.map(a => ({
      type: a.pointStyle || 'line',
      name: a.label,
      data: a.data.filter(a => a.x && a.y).map(a => [a.x, a.y / 1000]),
      color: a.borderColor,
      lineWidth: a.pointStyle === 'scatter' ? 0 : 2,
      dashStyle: a.pointStyle === 'line' ? 'Dash' : 'Solid',
      visible: !a.hidden,
      marker: {
        radius: 1,
        symbol: 'circle',
      },
    }));
    while (this.chartRef?.series?.length) {
      this.chartRef?.series[0].remove(false);
    }
    const manufacturerData = this.scatterChartData?.find(a => a?.label?.includes('Manufacturer'))?.data?.map(a => typeof a?.y === 'number' ? a?.y : 0) ?? [];
    const maxManufacturerPC = manufacturerData.length > 0 ? Math.max(...manufacturerData) : 0;
    this.chartOptions = Highcharts.merge(DEFAULT_CHART_OPTIONS, {
      chart: {
        type: 'line',
        height: this.reportHeight ?? 450,
        ...(!report && {
          events: {
            click: function () {
              component.emitSummaryClick(this.hoverPoint);
            },
          },
        }),
      },
      plotOptions: {
        series: {
          animation: { duration: 0 },
          states: {
            hover: {
              enabled: true,
              halo: { size: 0 },
            },
            inactive: { enabled: false },
          },
          ...(!report && {
            point: {
              events: {
                mouseOver: function () {
                  if (component.hideTooltip) {
                    component.emitSummary(this);
                  }
                },
                click: function () {
                  component.emitSummaryClick(this);
                },
              },
            },
          }),
        },
      },
      legend: { enabled: false },
      xAxis: {
        type: 'linear',
        tickColor: 'var(--divider)',
        lineWidth: 1,
        lineColor: 'var(--divider)',
        gridLineWidth: 1,
        gridLineColor: 'var(--divider)',
        min: 0,
        max: 25,
        title: {
          text: translate.instant('ANALYZE.PARK.WIND_SPEED'),
          y: 10,
        },
      },
      yAxis: {
        type: 'linear',
        tickColor: 'var(--divider)',
        lineWidth: 1,
        lineColor: 'var(--divider)',
        gridLineWidth: 1,
        gridLineColor: 'var(--divider)',
        min: 0,
        max: ((maxManufacturerPC || this.ratedPower) * 1.1) / 1000,
        title: {
          text: translate.instant('ANALYZE.PARK.OUTPUT_POWER'),
          x: -5,
        },
      },
      tooltip: {
        enabled: !this.hideTooltip,
        outside: true,
        followPointer: true,
        hideDelay: 0,
        shared: true,
        animation: { duration: 0 },
        useHTML: true,
        ...(!report && {
          padding: 0,
          borderRadius: 4,
          positioner: function (labelWidth, labelHeight, point) {
            const position = this.getPosition(labelWidth, labelHeight, point);
            const { left, top } = this.chart.pointer.getChartPosition();
            const minY = window.scrollY + TOOLTIP_WINDOW_MARGIN - this.distance;
            const maxY = window.scrollY + window.innerHeight - TOOLTIP_WINDOW_MARGIN - labelHeight - this.distance;
            const pageY = Math.max(minY, Math.min(position.y, maxY));
            return { x: position.x - (left - this.distance), y: pageY - (top - this.distance) };
          },
        }),
        formatter: function () {
          if (!report) {
            return getAnalysisTooltip(this);
          }
          let tooltipContent = '';
          if (!this.points) {
            tooltipContent += `
              <div class="font-weight-bold" style="margin-bottom: 5px; font-size: 12px">
              ${getSeriesSymbol(this.point.series)}
                <span style="color: var(--font)">${this.point.series.userOptions.name}</span>
              </div>
              <div style="margin-bottom: 10px; font-size: 12px">
                ${translate.instant('ANALYZE.PARK.WIND_SPEED')}: <b>${customNumberPipe.transform(Number(this.point.x).toFixed(2))}</b><br/>
                ${translate.instant('ANALYZE.PARK.OUTPUT_POWER')}: <b>${customNumberPipe.transform(Number(this.point.y).toFixed(2))}</b>
              </div>
            `;
          } else {
            this.points.map(p => {
              tooltipContent += `
                <div class="font-weight-bold" style="margin-bottom: 5px; font-size: 12px">
                ${getSeriesSymbol(p.series)}
                  <span style="color: var(--font)">${p.series.userOptions.name}</span>
                </div>
                <div style="margin-bottom: 10px; font-size: 12px">
                  ${translate.instant('ANALYZE.PARK.WIND_SPEED')}: <b>${customNumberPipe.transform(Number(p.x).toFixed(2))}</b><br/>
                  ${translate.instant('ANALYZE.PARK.OUTPUT_POWER')}: <b>${customNumberPipe.transform(Number(p.y).toFixed(2))}</b>
                </div>
              `;
            });
          }
          return tooltipContent;
        },
      },
      series: [],
    });
    setTimeout(() => {
      if (!this.chartRef?.options) return;
      series.forEach(s => {
        this.chartRef?.addSeries(s, false, false);
      });
      this.updateFlag = true;
    }, 100);
  }
}
